import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, signal, WritableSignal } from '@angular/core';
import { BusinessUnit } from '../../models/business-unit.model';
import { BusinessUnitService } from '../../services/business-unit.service';
import { MatDialog } from '@angular/material/dialog';
import { DeleteConfirmationComponent } from '../../../shared/components/dialogs/delete-confirmation/delete-confirmation.component';
import { Subscription } from 'rxjs';
import { LocationService } from '../../../shared/services/location.service';
import { LayoutService } from '../../../layouts/layout.service';
import { CompanyLocation } from '../../../locations/models/company-location';
// eslint-disable-next-line max-len
import { DeleteBusinessUnitWarningComponent } from '../../../shared/components/dialogs/business-unit/delete-business-unit-warning.component';
import { AuthService } from '../../../shared/auth.service';
import { TreeNode } from 'primeng/api';

@Component({
  selector: 'app-business-unit-list',
  templateUrl: './business-unit-list.component.html',
  styleUrls: ['./business-unit-list.component.scss'],
  standalone: false
})
export class BusinessUnitListComponent implements OnInit, OnDestroy {
  @Input() companyId!: string;
  public businessUnits!: BusinessUnit[];
  locations!: CompanyLocation[];
  subscriptions = new Array<Subscription>();
  loadingData = false;
  formIsDirty = false;

  treeNodes: WritableSignal<TreeNode[]> = signal([]);
  _treeNodes: TreeNode[] = [];

  constructor(private businessUnitService: BusinessUnitService,
    private locationService: LocationService,
    private layoutService: LayoutService,
    private authService: AuthService,
    public matAlert: MatDialog
  ) { }

  ngOnInit() {
    this.formIsDirty = false;
    this.getBusinessUnits();
    this.getLocations();
  }
  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
  getBusinessUnits() {
    this.loadingData = true;
    const sub = this.businessUnitService.getBusinessUnits(this.companyId).subscribe(res => {
      this.loadingData = false;
      this.businessUnits = res;
      this._treeNodes = res.map(bu => this.mapBusinessUnitToTreeNode(bu));
      this.treeNodes.set(this._treeNodes);
    }, error => {
      this.layoutService.showUIMessage('Unable to get business unit informations');
    });
    this.subscriptions.push(sub);
  }
  getLocations() {
    const sub = this.locationService
      .getLocationsForCompanyId(this.companyId)
      .subscribe(
        data => {
          this.locations = data;
        },
        error => {
          this.layoutService.showUIMessage('Unable to get business unit informations');
        }
      );

    this.subscriptions.push(sub);
  }
  addBusinessUnit(businessUnit: TreeNode) {
    if (!businessUnit.data) {
      return;
    }
    this.formIsDirty = true;

    if (businessUnit.data) {
      this.formIsDirty = true;
      businessUnit?.children?.push({ key: this.generateRandomStringValue(), children: [], expanded: true });
      this._treeNodes = [ ...this._treeNodes ];
      this.treeNodes.update(() => this._treeNodes);
    }
  }

  addRootBusinessUnit() {
    this.formIsDirty = true;
    this._treeNodes = [ ...this._treeNodes, { key: this.generateRandomStringValue(), children: [], expanded: true, data: '' } ];
    this.treeNodes.update(() => this._treeNodes);
  }

  onChangeBUText() {
    this.formIsDirty = true;
  }
  onDelete(businessUnit: TreeNode) {
    const businessUnitLocations = this.getBusinessUnitLocations(businessUnit.key);
    let sub: any;
    if (businessUnitLocations.length) {
      const locationNames = this.getLocationNames(businessUnitLocations);
      sub = this.matAlert
        .open(DeleteBusinessUnitWarningComponent, { data: { businessUnitName: businessUnit.data, locationNames } })
        .afterClosed()
        .subscribe(
          result => {
          }
        );

    } else {
      sub = this.matAlert
        .open(DeleteConfirmationComponent)
        .afterClosed()
        .subscribe(
          result => {
            if (result === true) {
              this.removeBusinessUnit(this._treeNodes, businessUnit);
              this._treeNodes = [ ...this._treeNodes ];
              this.treeNodes.update(() => this._treeNodes);
              this.formIsDirty = true;
            }
          }
        );
    }
    this.subscriptions.push(sub);
  }

  removeBusinessUnit(businessUnitsToSearch: TreeNode[], businessUnitToRemove: TreeNode) {
    this.formIsDirty = true;
    let indexOfFoundElement = -1;
    for (let i = 0; i < businessUnitsToSearch.length; i++) {
      if (businessUnitToRemove.key && businessUnitsToSearch[i].key === businessUnitToRemove.key) {
        indexOfFoundElement = i;
        break;
      }
      this.removeBusinessUnit(businessUnitsToSearch[i].children, businessUnitToRemove);
    }
    if (indexOfFoundElement > -1) {
      businessUnitsToSearch.splice(indexOfFoundElement, 1);
    }
  }

  getBusinessUnitLocations(businessUnitId: string): CompanyLocation[] {
    if (businessUnitId) {
      return this.locations.filter(x => x.businessUnitID === businessUnitId);
    }
    return [];
  }
  getLocationNames(locations: CompanyLocation[]): string {
    let locationName = '';
    for (let i = 0; i < locations.length; i++) {
      locationName += locations[i].locationName;
      if (i !== locations.length - 1) {
        locationName += ', ';
      }
    }
    return locationName;
  }

  saveBusinessUnits() {
    this.loadingData = true;
    this.businessUnitService.saveBusinessUnits(this.companyId, this.treeNodes().map(tn => this.mapTreeNodeToBusinessUnit(tn))).subscribe(res => {
      this.loadingData = false;
      this.layoutService.showUIMessage('Business units information successfully saved.');
      this.formIsDirty = false;
    }, err => {
      this.layoutService.showUIMessage('Unable to save business units information. Try reloading this page.');
    });
  }

  // helper private functions
  private generateRandomStringValue() {
    const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = 10; i > 0; --i) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  }

  private mapBusinessUnitToTreeNode(businessUnit: BusinessUnit): TreeNode {
    if (!businessUnit.businessUnits || businessUnit.businessUnits.length === 0) {
      return {
        key: businessUnit.businessUnitID,
        expanded: true,
        data: businessUnit.name,
        children: []
      }
    }
    return {
      key: businessUnit.businessUnitID,
      expanded: true,
      data: businessUnit.name,
      children: businessUnit.businessUnits.map(bu => this.mapBusinessUnitToTreeNode(bu))
    }
  }

  private mapTreeNodeToBusinessUnit(treeNode: TreeNode): BusinessUnit {
    if (!treeNode.children || treeNode.children.length === 0) {
      const businessUnit = new BusinessUnit();
      businessUnit.tempId = treeNode.key ?? '';
      businessUnit.name = treeNode.data;
      return businessUnit;
    }
    const businessUnit = new BusinessUnit();
    businessUnit.tempId = treeNode.key ?? '';
    businessUnit.name = treeNode.data;
    businessUnit.businessUnits = treeNode.children.map(tn => this.mapTreeNodeToBusinessUnit(tn));
    return businessUnit;
  }

  get isViewOnly() {
    return this.authService.isViewOnly;
  }
}
